import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { SpeakerWithDetails, Pronouns } from '@goldcast/player-config/src/types/speaker-types';
import { screenShareBarsColors, sharedVideoBarsColors } from '@goldcast/player-config/src/constants/speakerColors';
import { VideoFeed, VideoFeedType } from '@goldcast/player-config/src/types/video-feed-types';
import { SpeakersAnalysis } from '@goldcast/player-config/src/types/speakers-analysis';
import { getClipBroadcastId } from '@goldcast/player-config/src/utils/clipUtils';
import { useContentMetadataStore } from '../contentMetaDataStore';
import { isScreenShareDetectionFeatureFlagEnabled } from '@/stores/clip';

const collectStageTimeSpans = (feeds: VideoFeed[]): Record<number, number> => {
  const stageTimeSpans: Record<number, number> = {};

  feeds.forEach(feed => {
    feed.stageTimeSpans?.forEach(timeSpan => {
      stageTimeSpans[timeSpan.start] = timeSpan.end;
    });
  });

  return stageTimeSpans;
};

export const getScreenShareSpeakerDetails = (
  feeds: VideoFeed[],
  clip?: Clip,
  speakersAnalysisForAllClips?: Record<string, SpeakersAnalysis | undefined>
): SpeakerWithDetails | null => {
  const stageTimeSpans = collectStageTimeSpans(feeds.filter(feed => feed.type === VideoFeedType.SCREENSHARE));

  if (clip && isScreenShareDetectionFeatureFlagEnabled()) {
    const broadcastId = getClipBroadcastId(clip) ?? '';

    const speakersAnalysis =
      speakersAnalysisForAllClips?.[clip.id] ??
      useContentMetadataStore.getState().data[broadcastId]?.metadata?.speakerAnalysis;

    speakersAnalysis?.screenshare_bounds?.forEach(bound => {
      stageTimeSpans[bound.start_time] = bound.end_time;
    });
  }

  if (Object.keys(stageTimeSpans).length === 0) {
    return null;
  }

  const speakerDetails: SpeakerWithDetails = {
    first_name: 'Shared Screen',
    last_name: '',
    id: 'screen-share',
    key: 'screen-share',
    color: screenShareBarsColors.textColor,
    bgColor: screenShareBarsColors.bgColor,
    lightBgColor: screenShareBarsColors.lightBgColor,
    hoverBgColor: screenShareBarsColors.hoverBgColor,
    speakingSlots: stageTimeSpans,
    parent_id: '',
    company: '',
    title: '',
    profile_picture_url: '',
    pronouns: Pronouns.HE
  };

  return speakerDetails;
};

export const getSharedVideoTrackDetails = (feeds: VideoFeed[]): SpeakerWithDetails | null => {
  const sharedVideoFeeds = feeds.filter(feed => feed.type === VideoFeedType.SHARED_VIDEO);
  const stageTimeSpans = collectStageTimeSpans(sharedVideoFeeds);

  if (sharedVideoFeeds.length === 0) {
    return null;
  }

  const speakerDetails: SpeakerWithDetails = {
    first_name: 'Shared Video',
    last_name: '',
    id: 'shared-video',
    key: 'shared-video',
    color: sharedVideoBarsColors.textColor,
    bgColor: sharedVideoBarsColors.bgColor,
    lightBgColor: sharedVideoBarsColors.lightBgColor,
    hoverBgColor: sharedVideoBarsColors.hoverBgColor,
    speakingSlots: stageTimeSpans,
    parent_id: '',
    company: '',
    title: '',
    profile_picture_url: '',
    pronouns: Pronouns.HE
  };

  return speakerDetails;
};
